import { BookOpen, Building2, CreditCard, Bus, Monitor, Users, Globe } from 'lucide-react';
import React from 'react';

export type TicketStatus = 'Open' | 'In Progress' | 'Resolved' | 'Escalated';
export type TicketPriority = 'Normal' | 'High' | 'Urgent';

export interface DemoTicket {
    id: string;
    title: string;
    recentMessage: string;
    type: 'Normal' | 'Escalated';
    department: string;
    student: string;
    studentEmail: string;
    status: TicketStatus;
    raisedOn: string;
}

export interface DepartmentCard {
    name: string;
    description: string;
    color: string;
    icon: string; // emoji
}

export interface StatCard {
    label: string;
    value: number | string;
    trend: string;
    color: string; // accent color hex
}

// ── Stats ──────────────────────────────────────────────────────
export const DEMO_STATS_SCHOOL_ADMIN: StatCard[] = [
    { label: 'Total Tickets', value: 10, trend: '↑ 8% this week', color: '#f59e0b' },
    { label: 'Escalated', value: 3, trend: '↑ 11% this week', color: '#ef4444' },
    { label: 'Open', value: 3, trend: '↑ 13% this week', color: '#f59e0b' },
    { label: 'In Progress', value: 5, trend: '↑ 11% this week', color: '#818cf8' },
    { label: 'Resolved', value: 2, trend: '↑ 13% this week', color: '#10b981' },
];

export const DEMO_STATS_SUPER_ADMIN: StatCard[] = [
    { label: 'Total Schools', value: 12, trend: '↑ 2 this month', color: '#818cf8' },
    { label: 'Total Tickets', value: 84, trend: '↑ 15% this week', color: '#f59e0b' },
    { label: 'Escalated', value: 9, trend: '↓ 3% this week', color: '#ef4444' },
    { label: 'Active Users', value: 320, trend: '↑ 22 this week', color: '#10b981' },
    { label: 'Resolved', value: 61, trend: '↑ 18% this week', color: '#0ea5e9' },
];

export const DEMO_STATS_EMPLOYEE: StatCard[] = [
    { label: 'Assigned to Me', value: 6, trend: '↑ 2 today', color: '#818cf8' },
    { label: 'Escalated', value: 1, trend: 'Needs attention', color: '#ef4444' },
    { label: 'Open', value: 2, trend: '↑ 1 today', color: '#f59e0b' },
    { label: 'In Progress', value: 3, trend: 'Stable', color: '#0ea5e9' },
    { label: 'Resolved', value: 14, trend: '↑ 13% this week', color: '#10b981' },
];

// ── Department cards ───────────────────────────────────────────
export const DEMO_DEPARTMENTS: DepartmentCard[] = [
    { name: 'Academics', description: 'Curriculum, exams, grades & academic records', color: '#818cf8', icon: '🎓' },
    { name: 'Administration', description: 'Admissions, transfers, certificates & records', color: '#06b6d4', icon: '🏫' },
    { name: 'Accounts / Fees', description: 'Fee payment, receipts, scholarships & refunds', color: '#10b981', icon: '💳' },
    { name: 'Transport', description: 'Bus routes, passes, drivers & timing queries', color: '#f59e0b', icon: '🚌' },
    { name: 'IT / Support', description: 'Portal access, passwords, devices & software', color: '#ef4444', icon: '🖥️' },
];

// ── Ticket rows ────────────────────────────────────────────────
export const DEMO_TICKETS: DemoTicket[] = [
    {
        id: 'TKT-2024-001',
        title: 'Grade correction for Mathematics Mid-Term',
        recentMessage: 'Grade has been corrected. Please verify...',
        type: 'Normal',
        department: 'Academics',
        student: 'Ananya Singh',
        studentEmail: 'ananya.s@student.greenvalley.edu',
        status: 'Resolved',
        raisedOn: '10 Mar 2024',
    },
    {
        id: 'TKT-2024-002',
        title: 'Fee receipt not received for Q3 payment',
        recentMessage: 'Checking with the bank records. W...',
        type: 'Escalated',
        department: 'Accounts/Fees',
        student: 'Rohan Verma',
        studentEmail: 'rohan.v@student.greenvalley.edu',
        status: 'In Progress',
        raisedOn: '11 Mar 2024',
    },
    {
        id: 'TKT-2024-003',
        title: 'Bus route change request - Route 7B',
        recentMessage: 'Requesting a change from Route 5...',
        type: 'Normal',
        department: 'Transport',
        student: 'Preethi Rajan',
        studentEmail: 'preethi.r@student.greenvalley.edu',
        status: 'Open',
        raisedOn: '14 Mar 2024',
    },
    {
        id: 'TKT-2024-004',
        title: 'Student portal login issue',
        recentMessage: 'Unable to login to the student porta...',
        type: 'Escalated',
        department: 'IT/Support',
        student: 'Karthik Balasubramanian',
        studentEmail: 'karthik.b@student.greenvalley.edu',
        status: 'In Progress',
        raisedOn: '13 Mar 2024',
    },
    {
        id: 'TKT-2024-005',
        title: 'Inquiry about annual day costumes',
        recentMessage: 'When will the costumes for the gra...',
        type: 'Normal',
        department: 'Administration',
        student: 'Sanya Malhotra',
        studentEmail: 'sanya.m@parent.com',
        status: 'Open',
        raisedOn: '15 Mar 2024',
    },
    {
        id: 'TKT-2024-006',
        title: 'Scholarship application status',
        recentMessage: 'Waiting for scholarship status...',
        type: 'Normal',
        department: 'Accounts/Fees',
        student: 'Ishaan Gupta',
        studentEmail: 'ishaan.g@student.greenvalley.edu',
        status: 'In Progress',
        raisedOn: '08 Mar 2024',
    },
    {
        id: 'TKT-2024-007',
        title: 'Duplicate marksheet request',
        recentMessage: 'Need a duplicate marksheet for Ter...',
        type: 'Normal',
        department: 'Academics',
        student: 'Aarav Patel',
        studentEmail: 'aarav.p@student.greenvalley.edu',
        status: 'Open',
        raisedOn: '15 Mar 2024',
    },
    {
        id: 'TKT-2024-008',
        title: 'Bus tracker not showing Route 3',
        recentMessage: 'Bus tracker is down...',
        type: 'Normal',
        department: 'Transport',
        student: 'Meera Reddy',
        studentEmail: 'meera.r@parent.com',
        status: 'In Progress',
        raisedOn: '16 Mar 2024',
    },
    {
        id: 'TKT-2024-009',
        title: 'Curriculum query - Grade 10 Physics',
        recentMessage: 'Clarification on Physics syllabus...',
        type: 'Normal',
        department: 'Academics',
        student: 'Rahul Bose',
        studentEmail: 'rahul.b@student.greenvalley.edu',
        status: 'Resolved',
        raisedOn: '05 Mar 2024',
    },
    {
        id: 'TKT-2024-010',
        title: 'Admission cancellation & refund',
        recentMessage: 'Requesting refund...',
        type: 'Escalated',
        department: 'Administration',
        student: 'Zoya Khan',
        studentEmail: 'zoya.k@parent.com',
        status: 'In Progress',
        raisedOn: '12 Mar 2024',
    },
];

export const DEPT_COLOR_MAP: Record<string, string> = {
    'Academics': '#818cf8',
    'Administration': '#06b6d4',
    'Accounts/Fees': '#10b981',
    'Transport': '#f59e0b',
    'IT/Support': '#ef4444',
};

export const DEPT_ICON_MAP: Record<string, string> = {
    'Academics': '🎓',
    'Administration': '🏫',
    'Accounts/Fees': '💳',
    'Transport': '🚌',
    'IT/Support': '🖥️',
};
